import { isSpaHtmlPayload } from '@logicalcommander/slim-utility'
import { ClientErrorCodes } from '@logicalcommander/types'
import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError, retry } from '@reduxjs/toolkit/query'

const TRANSIENT_GATEWAY_HTTP_STATUSES = new Set([502, 503, 504])
const GET_RETRY_MAX = 2

// Custom error handler that extracts clientErrorCode
const handleApiError = (error: FetchBaseQueryError) => {
  // Extract clientErrorCode from the error response
  const errorData = (error as any)?.data || error
  const clientErrorCode = errorData?.clientErrorCode

  if (clientErrorCode && clientErrorCode !== 'GENERAL_ERROR') {
    // Return a custom error object that includes the clientErrorCode
    // This will be handled by the component's error handling
    return {
      ...error,
      clientErrorCode,
      isClientError: true,
    }
  }

  return error
}

/** Gateway/CDN misroute: SPA index.html with HTTP 200 instead of JSON (ECOMM-3438). */
export const buildSpaHtmlMisrouteError = (): FetchBaseQueryError =>
  handleApiError({
    status: 403,
    data: { clientErrorCode: ClientErrorCodes.COMPANY_EXPIRED_ERROR },
  }) as FetchBaseQueryError

export const isIdempotentGetRequest = (args: string | FetchArgs): boolean => {
  if (typeof args === 'string') {
    return true
  }
  const method = (args.method ?? 'GET').toUpperCase()
  return method === 'GET' || method === 'HEAD'
}

export const shouldRetryTransientGet = (
  args: string | FetchArgs,
  error: FetchBaseQueryError,
  attempt: number,
  maxRetries: number = GET_RETRY_MAX
): boolean => {
  if (!isIdempotentGetRequest(args)) {
    return false
  }
  if (attempt > maxRetries) {
    return false
  }
  return isTransientGatewayHttpStatus(getEffectiveHttpStatusFromFetchBaseQueryError(error))
}

/** HTTP status from RTK FetchBaseQueryError, including nginx HTML 502 (PARSING_ERROR + originalStatus). */
export const getEffectiveHttpStatusFromFetchBaseQueryError = (error: FetchBaseQueryError): number | undefined => {
  const direct = getFetchBaseQueryHttpStatus(error)
  if (direct !== undefined) {
    return direct
  }
  if (error.status === 'PARSING_ERROR') {
    const originalStatus = (error as FetchBaseQueryError & { originalStatus?: unknown }).originalStatus
    return typeof originalStatus === 'number' ? originalStatus : undefined
  }
  return undefined
}

export const isTransientGatewayHttpStatus = (status: number | undefined): boolean =>
  status !== undefined && TRANSIENT_GATEWAY_HTTP_STATUSES.has(status)

const shortBackoff = async (attempt: number): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, 250 * attempt))
}

// Create a base query with error handling
// @lat: [[state#Gateway base query]]
export const createBaseQueryWithErrorHandling = (baseUrl: string, prepareHeaders?: (headers: Headers, api: any) => void): BaseQueryFn => {
  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders,
  })

  const executeOnce: BaseQueryFn = async (args: FetchArgs, api: any, extraOptions: any) => {
    const result = await baseQuery(args, api, extraOptions)

    if (result.error) {
      const fetchError = result.error as FetchBaseQueryError
      if (fetchError.status === 'PARSING_ERROR' && isSpaHtmlPayload(fetchError.data)) {
        return { error: buildSpaHtmlMisrouteError() }
      }
      result.error = handleApiError(result.error)
      return result
    }

    if (isSpaHtmlPayload(result.data)) {
      return { error: buildSpaHtmlMisrouteError() }
    }

    return result
  }

  return retry(executeOnce, {
    maxRetries: GET_RETRY_MAX,
    backoff: shortBackoff,
    retryCondition: (error, args, { attempt }) =>
      shouldRetryTransientGet(args, error as FetchBaseQueryError, attempt),
  })
}

// Generic error handler for components to use
export const extractClientErrorCode = (error: unknown): ClientErrorCodes | undefined => {
  if (error && typeof error === 'object' && 'clientErrorCode' in error) {
    const clientErrorCode = (error as any).clientErrorCode
    return clientErrorCode === 'GENERAL_ERROR' ? undefined : clientErrorCode
  }

  // Check if error has a data property with clientErrorCode
  if (error && typeof error === 'object' && 'data' in error) {
    const errorData = (error as any).data
    const clientErrorCode = errorData?.clientErrorCode
    return clientErrorCode === 'GENERAL_ERROR' ? undefined : clientErrorCode
  }

  return undefined
}

/** HTTP status from an RTK Query `.unwrap()` rejection (FetchBaseQueryError shape). */
export const getFetchBaseQueryHttpStatus = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status
    return typeof status === 'number' ? status : undefined
  }
  return undefined
}
